import BusAlertIcon from '@mui/icons-material/BusAlert'
import { Box, Checkbox, CircularProgress, MenuItem, Stack, Typography } from '@mui/material'
import { createFileRoute } from '@tanstack/react-router'
import { DateTime } from 'luxon'
import { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MapRenderContext } from 'src/contexts/MapRenderContext'
import {
    useGetTripGeometryTripsTripIdGeometryGet,
    useGetTripTripsTripIdGet,
    useGetVehicleLocationsTripsTripIdVehiclesGet,
} from 'src/generated/brezavta/queries'

import AlertDrawer from 'src/components/AlertDrawer'
import { LineChip } from 'src/components/LineChip'
import AutomaticSheet from 'src/components/sheet/AutomaticSheet'
import SheetHeader from 'src/components/sheet/SheetHeader'
import TripArrivals from 'src/components/TripArrivals'
import { mapSetPaintProperty } from 'src/lib/util'

export const Route = createFileRoute('/trip/$tripId')({ component: Trip })

const VEHICLE_LOCATION_REFRESH_INTERVAL = 3000

function Trip() {
    const { map, mapLoaded } = useContext(MapRenderContext)
    const { tripId } = Route.useParams()
    const { t } = useTranslation()

    const [menuItems, setMenuItems] = useState<MenuItem[]>([])
    const [isFollowing, setIsFollowing] = useState(true)

    const { data: tripData, error: tripDataError, isLoading: tripDataLoading, refetch: refetchTrip } = useGetTripTripsTripIdGet({
        path: { trip_id: tripId },
        query: { date: DateTime.now().toFormat('yyyyMMdd') },
    })
    const { data: polylineData } = useGetTripGeometryTripsTripIdGeometryGet({ path: { trip_id: tripId } })
    const { data: vehicleLocationData, refetch: refetchVehicles } = useGetVehicleLocationsTripsTripIdVehiclesGet({
        path: { trip_id: tripId },
    })

    // Auto refreshing

    useEffect(() => {
        const interval = setInterval(() => {
            refetchTrip()
            refetchVehicles()
        }, VEHICLE_LOCATION_REFRESH_INTERVAL)

        return () => clearInterval(interval)
    }, [refetchTrip, refetchVehicles])

    // Polyline

    const updatePolylineLayer = () => {
        const mapInstance = map?.current
        if (mapInstance?.getSource('tripPolyline')) {
            mapInstance.getSource('tripPolyline').setData({
                type: 'FeatureCollection',
                features: [{
                    type: 'Feature',
                    geometry: { type: 'LineString', coordinates: polylineData.coordinates },
                    properties: { color: polylineData.properties.color },
                }],
            })
        }
    }

    useEffect(() => {
        if (mapLoaded && polylineData) {
            updatePolylineLayer()
        }
    }, [polylineData, mapLoaded])

    // Vehicle locations

    const updateVehicleLayer = () => {
        const mapInstance = map?.current
        if (mapInstance?.getSource('vehicles')) {
            mapInstance.getSource('vehicles').setData({
                type: 'FeatureCollection',
                features: vehicleLocationData.map(vehicle => ({
                    type: 'Feature',
                    geometry: { type: 'Point', coordinates: [vehicle.lon, vehicle.lat] },
                    properties: { color: vehicle.color },
                })),
            })
            if (isFollowing && vehicleLocationData.length > 0) {
                mapInstance.flyTo({
                    center: [vehicleLocationData[0].lon, vehicleLocationData[0].lat],
                    zoom: 15,
                })
            }
        }
    }

    useEffect(() => {
        if (mapLoaded && vehicleLocationData) {
            updateVehicleLayer()
        }
    }, [vehicleLocationData, isFollowing, mapLoaded])

    // Menu items

    useEffect(() => {
        if (!vehicleLocationData || vehicleLocationData.length !== 0)
            setMenuItems([])
        setMenuItems([
            <MenuItem onClick={() => setIsFollowing(f => !f)} key="follow">
                <Checkbox checked={isFollowing} />
                {' '}
                {t('Follow vehicle')}
            </MenuItem>,
        ])
    }, [vehicleLocationData, isFollowing])

    // Emphasize the current layer
    useEffect(() => {
        if (!mapLoaded) return
        mapSetPaintProperty(map?.current, 'stops-symbol-', 'icon-opacity', 0.5)
        mapSetPaintProperty(map?.current, 'micromobility-icon-', 'icon-opacity', 0.5)
        mapSetPaintProperty(map?.current, 'micromobility-detail-', 'icon-opacity', 0.5)
        return () => {
            mapSetPaintProperty(map?.current, 'stops-symbol-', 'icon-opacity', 1)
            mapSetPaintProperty(map?.current, 'micromobility-icon-', 'icon-opacity', 1)
            mapSetPaintProperty(map?.current, 'micromobility-detail-', 'icon-opacity', 1)
        }
    }, [mapLoaded])

    if (tripDataLoading) {
        return (
            <AutomaticSheet>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: window.innerHeight / 2 }}>
                    <CircularProgress />
                </Box>
            </AutomaticSheet>
        )
    }

    if (tripDataError || !tripData) {
        return (
            <AutomaticSheet>
                <Stack sx={{ p: 5 }}>{t('Error loading trip details.')}</Stack>
            </AutomaticSheet>
        )
    }

    const hasAlerts = trip => trip?.stop_times?.some(stopTime => stopTime.alerts.length > 0)

    return (
        <AutomaticSheet>
            <Stack>
                <SheetHeader
                    heading={(
                        <>
                            <LineChip
                                text={tripData.route_short_name}
                                color={tripData.color}
                                textColor={tripData.text_color}
                                extraStyles={{ marginRight: 5 }}
                            />
                            {tripData?.trip_headsign}
                            {hasAlerts(tripData) && <BusAlertIcon sx={{ color: 'orange' }} />}
                        </>
                    )}
                    menuItems={menuItems}
                >
                    {(vehicleLocationData && vehicleLocationData.length > 0) && (
                        <Typography variant="subtitle2">
                            {`${t('Vehicle')}: ${vehicleLocationData.map(v => v.vehicle.plate || v.vehicle.id).join(', ')}`}
                        </Typography>
                    )}
                </SheetHeader>
                <Box sx={{ p: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <AlertDrawer tripData={tripData} />
                    <TripArrivals tripData={tripData} />
                </Box>
            </Stack>
        </AutomaticSheet>
    )
}

export default Trip
